import { Button, Stack, Typography } from '@sureco/design-system'
import { Input, Modal, Select } from '@surecompanies/core_components'
import { isEmpty } from 'lodash'
import React, { memo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ActivityReasonCategory } from 'types/activity'
import { closeActivityLogPopup } from '../activityLogPopupSlice'
import { ReasonCategories } from '../constants'

interface FormData {
  reason?: string
  reasonCategory?: ActivityReasonCategory | ''
  zendeskTicket?: string
}

interface RootState {
  activityLogPopupSlice: {
    open: boolean
    contentText?: string
    onCancel?: () => void
    onSubmit?: (formData: FormData) => void
    required: boolean
  }
}

const ActivityLogPopup: React.FC = () => {
  const dispatch = useDispatch()
  const { open, contentText, onCancel, onSubmit, required } = useSelector(
    (state: RootState) => state.activityLogPopupSlice
  )

  const [formData, setFormData] = useState<FormData>({
    reason: '',
    reasonCategory: '',
    zendeskTicket: '',
  })

  const [showErrors, setShowErrors] = useState(false)

  const handleCancel = () => {
    onCancel?.()

    setFormData({
      reason: '',
      reasonCategory: '',
      zendeskTicket: '',
    })
    setShowErrors(false)
    dispatch(closeActivityLogPopup())
  }

  const validateForm = () => {
    if (!required) return true

    return !isEmpty(formData.reasonCategory) && !isEmpty(formData.reason)
  }

  const getFieldError = (fieldName: string) => {
    if (!required || !showErrors) return false

    switch (fieldName) {
      case 'reasonCategory':
        return isEmpty(formData.reasonCategory)
      case 'reason':
        return isEmpty(formData.reason)
      default:
        return false
    }
  }

  const handleSubmit = () => {
    if (required && !validateForm()) {
      setShowErrors(true)
      return
    }

    onSubmit?.(formData)

    setFormData({
      reason: '',
      reasonCategory: '',
      zendeskTicket: '',
    })
    setShowErrors(false)
    dispatch(closeActivityLogPopup())
  }

  const handleClose = () => {
    handleCancel()
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))

    // Clear errors when user starts typing
    if (showErrors) {
      setShowErrors(false)
    }
  }

  return (
    <Modal
      open={open ?? false}
      onCancel={handleClose}
      footer={null}
      closable
      maskClosable
      width={600}
      zIndex={1002} // Workaround to avoid being covered by other modals
      centered
    >
      <Stack space={20}>
        <Stack space={0}>
          <Typography as="p" size={16} bold>
            {contentText || 'Are you sure you want to do this action?'}
          </Typography>
        </Stack>

        <Stack space={16}>
          <div>
            <Typography as="span" size={14}>
              Reason Category{' '}
              {required && <span style={{ color: '#ff4d4f' }}>*</span>}
            </Typography>
            <Select
              placeholder="Select a reason category"
              value={formData.reasonCategory}
              onChange={(value) => handleInputChange('reasonCategory', value)}
              style={{
                width: '100%',
                marginTop: 8,
                borderColor: getFieldError('reasonCategory')
                  ? '#ff4d4f'
                  : undefined,
              }}
              status={getFieldError('reasonCategory') ? 'error' : undefined}
            >
              {ReasonCategories.map((category) => (
                <Select.Option key={category.value} value={category.value}>
                  {category.label}
                </Select.Option>
              ))}
            </Select>
            {getFieldError('reasonCategory') && (
              <Typography
                as="span"
                size={12}
                style={{ color: '#ff4d4f', marginTop: 4 }}
              >
                Reason category is required
              </Typography>
            )}
          </div>

          <div>
            <Typography as="span" size={14}>
              Reason {required && <span style={{ color: '#ff4d4f' }}>*</span>}
            </Typography>
            <Input.TextArea
              placeholder="Enter the reason for this activity log"
              value={formData.reason}
              onChange={(e) => handleInputChange('reason', e.target.value)}
              rows={3}
              style={{ marginTop: 8 }}
              status={getFieldError('reason') ? 'error' : undefined}
            />
            {getFieldError('reason') && (
              <Typography
                as="span"
                size={12}
                style={{ color: '#ff4d4f', marginTop: 4 }}
              >
                Reason is required
              </Typography>
            )}
          </div>

          <div>
            <Typography as="span" size={14}>
              Zendesk Ticket{' '}
            </Typography>
            <Input
              placeholder="Enter Zendesk ticket number"
              value={formData.zendeskTicket}
              onChange={(e) =>
                handleInputChange('zendeskTicket', e.target.value)
              }
              style={{ marginTop: 8 }}
            />
          </div>
        </Stack>

        <Stack direction="row" space={10} justify="end">
          <Button type="outline" variant="secondary" onClick={handleCancel}>
            Cancel
          </Button>
          <Button type="flat" variant="primary" onClick={handleSubmit}>
            Submit
          </Button>
        </Stack>
      </Stack>
    </Modal>
  )
}

export default memo(ActivityLogPopup)
